import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { QueryParams } from '../../types/common/queryParams';

type Options = {
  query?: QueryParams;
};

const useRouter = (): {
  push: (path: string, options?: Options) => void;
  replace: (path: string, options: Options) => void;
  pathname: string;
  query: QueryParams;
} => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const params: QueryParams = {};

    searchParams.forEach((value, key) => {
      if (!params[key]) {
        params[key] = value;
        return;
      }

      if (Array.isArray(params[key])) {
        (params[key] as string[]).push(value);
        return;
      }

      params[key] = [params[key] as string, value];
    });

    return params;
  }, [location.search]);

  const updateNavigation = useCallback(
    (method: 'push' | 'replace', pathname: string, options?: Options) => {
      const searchParams = new URLSearchParams();

      const { query } = options ?? {};

      if (query) {
        Object.entries(query).forEach(([key, value]) => {
          if (value === undefined) {
            return;
          }

          if (Array.isArray(value)) {
            value.forEach((value) => searchParams.append(key, value));
            return;
          }

          searchParams.set(key, value);
        });
      }

      const search = searchParams.toString();
      const to = search ? `${pathname}?${search}` : pathname;

      if (method === 'replace') {
        navigate(to, { replace: true });
      } else {
        navigate(to);
      }
    },
    [navigate],
  );

  const push = useCallback(
    (pathname: string, options?: Options): void => {
      updateNavigation('push', pathname, options);
    },
    [updateNavigation],
  );

  const replace = useCallback(
    (pathname: string, options: Options): void => {
      updateNavigation('replace', pathname, options);
    },
    [updateNavigation],
  );

  return {
    push,
    replace,
    pathname: location.pathname,
    query,
  };
};

export default useRouter;
